// Share / Download PNG / Copy link row used by ShareStatusModal and the
// Property overview badge. Same three interactions as the RB UX badge modal:
// native share sheet, PNG download, and copy of the signed preview URL.
import React, { useCallback, useState } from 'react';
import { Share2, Download, Link2, Check } from 'lucide-react';
import { EcointelLottieMark } from './EcoIntelLoader';

function sanitizeForFilename(name) {
  return String(name || 'property')
    .trim()
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'property';
}

export default function ShareStatusActions({ badgeUrl, propertyName, className = '' }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [actionNote, setActionNote] = useState(null);

  const filename = `EcoIntel_${sanitizeForFilename(propertyName)}_status.png`;

  const fetchBadgeBlob = useCallback(async () => {
    if (!badgeUrl) return null;
    try {
      const resp = await fetch(badgeUrl);
      if (!resp.ok) throw new Error(`badge fetch ${resp.status}`);
      return await resp.blob();
    } catch (e) {
      console.warn('[ShareStatusActions] badge blob fetch failed', e?.message || e);
      return null;
    }
  }, [badgeUrl]);

  const handleDownload = useCallback(async () => {
    if (!badgeUrl) return;
    setActionNote(null);
    const blob = await fetchBadgeBlob();
    if (blob) {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
      return;
    }
    const a = document.createElement('a');
    a.href = badgeUrl;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [badgeUrl, fetchBadgeBlob, filename]);

  const handleCopyLink = useCallback(async () => {
    if (!badgeUrl) return;
    setActionNote(null);
    try {
      await navigator.clipboard.writeText(badgeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.warn('[ShareStatusActions] clipboard write failed', e?.message || e);
      setActionNote('Could not copy automatically. Long-press or right-click the image to copy the link.');
    }
  }, [badgeUrl]);

  const handleShare = useCallback(async () => {
    if (!badgeUrl) return;
    setActionNote(null);
    const shareTitle = propertyName ? `${propertyName} · land status` : 'My land status';
    const shareText = 'My land status, from EcoIntel.';
    setSharing(true);
    try {
      const blob = await fetchBadgeBlob();
      if (blob && navigator.canShare) {
        const file = new File([blob], filename, { type: blob.type || 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: shareTitle, text: shareText });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: badgeUrl });
        return;
      }
      await handleCopyLink();
    } catch (e) {
      if (e?.name !== 'AbortError') {
        console.warn('[ShareStatusActions] share failed', e?.message || e);
        setActionNote('Sharing is not available here. Use Copy link or Download instead.');
      }
    } finally {
      setSharing(false);
    }
  }, [badgeUrl, fetchBadgeBlob, filename, propertyName, handleCopyLink]);

  if (!badgeUrl) return null;

  return (
    <div className={className}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleShare}
          disabled={sharing}
          className="inline-flex items-center gap-2 rounded-lg bg-[#043132] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#063f3a] disabled:opacity-60"
        >
          {sharing ? <EcointelLottieMark size={16} /> : <Share2 size={15} />}
          Share
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <Download size={15} />
          Download PNG
        </button>
        <button
          type="button"
          onClick={handleCopyLink}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          {copied ? <Check size={15} className="text-emerald-600" /> : <Link2 size={15} />}
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      {actionNote ? (
        <p className="mt-3 text-xs text-amber-700">{actionNote}</p>
      ) : (
        <p className="mt-3 text-xs text-slate-400">
          The link is a private, time-limited preview. Download the PNG to share it anywhere.
        </p>
      )}
    </div>
  );
}
